import {DataTable} from "../DataTable/DataTable";
import {ServiceEventEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useServiceEventDataTableFilter} from "../ServiceEventDataTable/useServiceEventDataTableFilter";
import {DataTableSorterConfig, useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {useNavigate} from "react-router-dom";
import {useServiceEventAction} from "../../menu/ServiceEventActionMenu/useServiceEventAction";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {Badge} from "../../badge/Badge/Badge";
import { EntityAnchor } from "../../anchor/EntityAnchor/EntityAnchor";

const EMPTY_ARRAY: ServiceEventEntity[] = []

export type ServiceEventDataTableProps = EntityDataTableProps<ServiceEventEntity>

export const ServiceEventDataTable = (props: ServiceEventDataTableProps) => {
  const {
    records: serviceEvents = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useServiceEventDataTableFilter({
    records: serviceEvents
  })

  const sortConfig: DataTableSorterConfig<ServiceEventEntity> = useMemo(() => {
    return {
      "eventType": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.eventType?.code?.toLowerCase()
          }])
        }
      },
      "company": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.companyName?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter<ServiceEventEntity>({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'startDttm', direction: 'desc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    onServiceEventActionClick
  } = useServiceEventAction()

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: ServiceEventEntity) => {
    e.stopPropagation()
    navigate(`/service-events/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: ServiceEventEntity) => {
    e.stopPropagation()
    onServiceEventActionClick(record, 'EDIT')
  }, [onServiceEventActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: ServiceEventEntity) => {
    e.stopPropagation()
    onServiceEventActionClick(record, 'DELETE')
  }, [onServiceEventActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<ServiceEventEntity>[] = [
      {
        title: '',
        accessor: "active",
        width: 40,
        render: (record) => {
          if (record.isActive()) {
            return <Badge color="green">{t("common.status.active", "Active")}</Badge>
          } else {
            return <Badge color="gray">{t("common.status.inactive", "Inactive")}</Badge>
          }
        }
      },
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.serviceevent.table.column.name", "Name"),
        accessor: 'name',
        width: 100,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record} />
        ),
      },
      {
        title: t("common.entity.serviceevent.table.column.startdttm", "Start"),
        accessor: "startDttm",
        width: 100,
        sortable: true,
        render: ({startDttm}) => (startDttm ? <FormattedDateTime date={startDttm}/> : undefined)
      },
      {
        title: t("common.entity.serviceevent.table.column.enddttm", "End"),
        accessor: "endDttm",
        width: 100,
        sortable: true,
        render: ({endDttm}) => (endDttm ? <FormattedDateTime date={endDttm}/> : undefined)
      },
      {
        ...getColumnFilterProps(filterConfig, "eventType"),
        title: t("common.entity.serviceevent.table.column.eventtype", "Event Type"),
        accessor: 'eventType',
        width: 100,
        sortable: true,
        render: ({eventType}) => (
            <FormattedDatabaseCode value={eventType}/>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "company"),
        title: t("common.entity.serviceevent.table.column.company", "Company"),
        accessor: 'companyId',
        width: 100,
        sortable: true,
        render: ({companyId, companyName}) => (
          <EntityAnchor value={{ objectId: companyId, objectType: "COMPANY", objectName: companyName }} />
        ),
      },
      {
        title: '',
        accessor: "actions",
        width: 65,
        textAlignment: 'right',
        render: (serviceEvent) => (
            <EntityActionGroup
                permissionPrefix="serviceevent"
                onViewActionClick={(e) => onViewActionClick(e, serviceEvent)}
                onEditActionClick={(e) => onEditActionClick(e, serviceEvent)}
                onDeleteActionClick={(e) => onDeleteActionClick(e, serviceEvent)}/>
        )
      }
    ]

    return values
  }, [filterConfig, onDeleteActionClick, onEditActionClick, onViewActionClick, t])

  return (
      <DataTable
          {...rest}
          {...(pagination ?? {})}
          sortStatus={sortStatus}
          onSortStatusChange={onSortStatusChange}
          columns={columns}
      />
  )
}
